import { z } from "zod";
import { Id, IsoDate } from "./common.ts";
import type { RouteDef } from "./routes.ts";

/**
 * ALP-86: AI activity per org / user (admin, read from Langfuse) and per-org model routing with client-owned keys (BYOK).
 *
 * - AI activity: the API proxies the Langfuse public API (v4, events-only: `/api/public/v2/observations` + `/api/public/v2/metrics`)
 *   with the server keys; keys never reach the browser. Every AI call is traced with Langfuse user `<orgId>:<userId>`
 *   (`<orgId>:system` for automations) and the feature as the trace name.
 * - BYOK: an org's provider connection becomes org-private LiteLLM deployments (`byok-<orgId>-<suffix>`, keys stored by
 *   LiteLLM, encrypted with its salt key) reachable only through the org's own BYOK virtual key. Every org's Malfak key is
 *   scoped to `malfak-*`, so no other org can call them. Routing picks, per use case, the platform alias or an org model.
 */

// ---------------------------------------------------------------- AI activity (Langfuse)
export const AiCallStatus = z.enum(["ok", "error"]);
export const AiActivityQuery = z.object({
  orgId: Id.optional(),
  userId: Id.optional(),
  /** Default: the last 7 days. */
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  /** Feature = the Langfuse trace name (e.g. comment-classifier, reply-drafter, caption, copilot.turn). */
  feature: z.string().trim().min(1).max(80).optional(),
  /** Model as Langfuse records it (send back a `model` value from a row or the usage summary). */
  model: z.string().trim().min(1).max(160).optional(),
  status: AiCallStatus.optional(),
  /** Opaque cursor from the previous page. */
  cursor: z.string().max(4000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type AiActivityQueryT = z.infer<typeof AiActivityQuery>;
export const AiUsageQuery = AiActivityQuery.pick({ orgId: true, userId: true, from: true, to: true, feature: true, model: true });

export const AiTokens = z.object({ input: z.number().nullable(), output: z.number().nullable(), total: z.number().nullable() });
/** Whether tracing is readable here (Langfuse URL + keys configured); `reason` explains a disabled state. */
export const AiActivityState = z.object({
  configured: z.boolean(),
  reason: z.string().nullable(),
  /** Langfuse project home (deep links), null when not configured. */
  langfuseUrl: z.string().nullable(),
});
/** One AI call (a root observation in Langfuse: a model call, a Copilot turn, a Dify run). */
export const AiTraceRow = z.object({
  id: z.string(),
  traceId: z.string(),
  /** The trace name (feature), e.g. comment-classifier. */
  feature: z.string(),
  name: z.string(),
  type: z.string(),
  startTime: IsoDate,
  endTime: IsoDate.nullable(),
  latencyMs: z.number().nullable(),
  status: AiCallStatus,
  level: z.string(),
  statusMessage: z.string().nullable(),
  orgId: z.string().nullable(),
  /** Malfak user id, or null for automations (`system`). */
  userId: z.string().nullable(),
  system: z.boolean(),
  sessionId: z.string().nullable(),
  /** Model as recorded (provider name scrubbed); `modelName` is the neutral display name. */
  model: z.string().nullable(),
  modelName: z.string().nullable(),
  /** Tokens / cost of the whole trace (summed over its model calls). */
  tokens: AiTokens,
  costUsd: z.number().nullable(),
  /** Observations in the trace (steps). */
  steps: z.number().int(),
  tags: z.array(z.string()),
  environment: z.string().nullable(),
  /** BYOK: served by one of the org's own models. */
  byok: z.boolean(),
  langfuseUrl: z.string().nullable(),
});
export type AiTraceRowT = z.infer<typeof AiTraceRow>;
export const AiTracePage = AiActivityState.extend({ items: z.array(AiTraceRow), nextCursor: z.string().nullable() });
export type AiTracePageT = z.infer<typeof AiTracePage>;
/** One step of a trace; input/output/metadata are scrubbed (tokens, keys, provider names) and truncated. */
export const AiObservation = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  depth: z.number().int(),
  type: z.string(),
  name: z.string(),
  startTime: IsoDate,
  endTime: IsoDate.nullable(),
  latencyMs: z.number().nullable(),
  status: AiCallStatus,
  level: z.string(),
  statusMessage: z.string().nullable(),
  model: z.string().nullable(),
  modelName: z.string().nullable(),
  tokens: AiTokens,
  costUsd: z.number().nullable(),
  input: z.string().nullable(),
  output: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});
export type AiObservationT = z.infer<typeof AiObservation>;
export const AiTraceDetail = AiActivityState.extend({
  traceId: z.string(),
  root: AiTraceRow.nullable(),
  observations: z.array(AiObservation),
  totals: z.object({ tokens: AiTokens, costUsd: z.number().nullable(), latencyMs: z.number().nullable(), steps: z.number().int() }),
});
export type AiTraceDetailT = z.infer<typeof AiTraceDetail>;
export const AiUsageBucket = z.object({ key: z.string(), label: z.string(), calls: z.number(), tokens: z.number(), costUsd: z.number(), errors: z.number() });
export const AiUsageDay = z.object({ day: z.string(), calls: z.number(), tokens: z.number(), costUsd: z.number() });
export const AiUsageSummary = AiActivityState.extend({
  from: IsoDate,
  to: IsoDate,
  /** calls = model calls (generations); errors = calls that failed. */
  totals: z.object({ calls: z.number(), tokens: z.number(), costUsd: z.number(), errors: z.number() }),
  byFeature: z.array(AiUsageBucket),
  byModel: z.array(AiUsageBucket),
  byDay: z.array(AiUsageDay),
});
export type AiUsageSummaryT = z.infer<typeof AiUsageSummary>;

// ---------------------------------------------------------------- BYOK: use cases, connections, routing
export const AI_USE_CASES = ["classify", "reply_draft", "caption", "copilot", "image", "embed", "agents"] as const;
export const AiUseCase = z.enum(AI_USE_CASES);
export type AiUseCaseT = (typeof AI_USE_CASES)[number];
export const AiModelKind = z.enum(["chat", "embedding", "image"]);
export type AiModelKindT = z.infer<typeof AiModelKind>;
export interface UseCaseDef {
  /** Platform alias used when the org has no route. */
  alias: string;
  kind: AiModelKindT;
  /**
   * false = always the platform model: Copilot runs as a Dify chatflow and search-by-meaning embeds inside Dify Knowledge,
   * both with Dify's one platform provider (Dify can't pick a model or key per request).
   */
  routable: boolean;
}
export const USE_CASES: Record<AiUseCaseT, UseCaseDef> = {
  classify: { alias: "malfak-fast", kind: "chat", routable: true },
  reply_draft: { alias: "malfak-fast", kind: "chat", routable: true },
  caption: { alias: "malfak-smart", kind: "chat", routable: true },
  copilot: { alias: "malfak-smart", kind: "chat", routable: false },
  image: { alias: "malfak-image", kind: "image", routable: true },
  embed: { alias: "malfak-embed", kind: "embedding", routable: false },
  agents: { alias: "malfak-smart", kind: "chat", routable: true },
};
/** Langfuse trace name / AI task → use case (routing + the activity view's grouping). */
export const TASK_USE_CASE: Record<string, AiUseCaseT> = {
  "comment-classifier": "classify",
  "reply-drafter": "reply_draft",
  caption: "caption",
  "post-score": "caption",
  "image-generate": "image",
  "image-edit": "image",
  "copilot.turn": "copilot",
  embed: "embed",
  "dm-agent": "agents",
  agent: "agents",
};

export const AiProviderKind = z.enum(["openai", "anthropic", "azure_openai", "openai_compatible"]);
export type AiProviderKindT = z.infer<typeof AiProviderKind>;
/** Platform model aliases every org key may call (LiteLLM key `models` wildcard); org deployments never match it. */
export const PLATFORM_ALIAS_PATTERN = "malfak-*";
export const BYOK_DEPLOYMENT_PREFIX = "byok-";

export const OrgAiModel = z.object({
  id: Id,
  connectionId: Id,
  /** Provider model id as the client gave it, e.g. gpt-4o-mini or an Azure deployment name. */
  model: z.string(),
  label: z.string(),
  kind: AiModelKind,
  /** USD per 1M tokens, for BYOK spend estimates (null = the gateway's price list, else 0). */
  inputCostPerMTok: z.number().nullable(),
  outputCostPerMTok: z.number().nullable(),
  /** ready = deployment exists in the gateway. */
  status: z.enum(["ready", "error"]),
  lastError: z.string().nullable(),
});
export type OrgAiModelT = z.infer<typeof OrgAiModel>;
export const OrgAiConnection = z.object({
  id: Id,
  orgId: Id,
  name: z.string(),
  provider: AiProviderKind,
  /** Host of the base URL only (no path, query or credentials); null = the provider's default. */
  apiHost: z.string().nullable(),
  apiVersion: z.string().nullable(),
  /** Masked key ("sk-…a1b2"); the key itself is never returned. */
  keyHint: z.string().nullable(),
  models: z.array(OrgAiModel),
  status: z.enum(["untested", "ok", "error"]),
  lastTestAt: IsoDate.nullable(),
  lastError: z.string().nullable(),
  createdAt: IsoDate,
  createdBy: z.string().nullable(),
  rotatedAt: IsoDate.nullable(),
});
export type OrgAiConnectionT = z.infer<typeof OrgAiConnection>;
export const OrgAiRoute = z.object({
  useCase: AiUseCase,
  routable: z.boolean(),
  kind: AiModelKind,
  /** The platform alias used by default. */
  defaultAlias: z.string(),
  /** platform = the default alias; org_model = `modelId`. */
  target: z.enum(["platform", "org_model"]),
  modelId: Id.nullable(),
});
export type OrgAiRouteT = z.infer<typeof OrgAiRoute>;
export const ByokUnavailableReason = z.enum(["feature_off", "no_gateway_db", "gateway_unreachable"]);
export const OrgAiModelsAdmin = z.object({
  orgId: Id,
  orgName: z.string(),
  /** The org has the `byok` plan feature (admin override). */
  byokEnabled: z.boolean(),
  /** Connections can be created/used here (feature on + LiteLLM with a database). */
  available: z.boolean(),
  unavailableReason: ByokUnavailableReason.nullable(),
  connections: z.array(OrgAiConnection),
  routes: z.array(OrgAiRoute),
  spend: z.object({
    /** This month, Malfak's AI budget (`ai_budget_usd`). */
    malfakUsd: z.number(),
    /** This month, on the org's own keys (not counted in the AI budget). */
    byokUsd: z.number(),
    byokTokens: z.number(),
  }),
  keys: z.object({
    /** The org's Malfak key is limited to the platform aliases (`malfak-*`). */
    mainScoped: z.boolean(),
    /** Masked BYOK virtual key (gateway key for the org's own models), null until the first connection. */
    byokKeyHint: z.string().nullable(),
  }),
});
export type OrgAiModelsAdminT = z.infer<typeof OrgAiModelsAdmin>;

const Reason = z.string().trim().min(3).max(500);
export const OrgAiModelInput = z.object({
  model: z.string().trim().min(1).max(200),
  label: z.string().trim().min(1).max(80).optional(),
  kind: AiModelKind.default("chat"),
  inputCostPerMTok: z.number().min(0).max(10_000).nullable().optional(),
  outputCostPerMTok: z.number().min(0).max(10_000).nullable().optional(),
});
const needsBase = (p: AiProviderKindT) => p === "azure_openai" || p === "openai_compatible";
export const CreateAiConnectionRequest = z
  .object({
    name: z.string().trim().min(1).max(80),
    provider: AiProviderKind,
    /** The client's provider key (write-only: encrypted at rest, only a masked hint is ever returned). */
    apiKey: z.string().trim().min(8).max(500),
    apiBase: z.string().url().max(500).optional(),
    apiVersion: z.string().trim().max(40).optional(),
    models: z.array(OrgAiModelInput).min(1).max(10),
    reason: Reason,
  })
  .refine((v) => !needsBase(v.provider) || !!v.apiBase, { path: ["apiBase"], message: "A base URL is required for this provider" });
export const UpdateAiConnectionRequest = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  /** Replaces the model list (existing models keep their id when `model` is unchanged). */
  models: z.array(OrgAiModelInput).min(1).max(10).optional(),
  reason: Reason,
});
export const RotateAiConnectionRequest = z.object({ apiKey: z.string().trim().min(8).max(500), reason: Reason });
export const AiConnectionTestResult = z.object({
  ok: z.boolean(),
  latencyMs: z.number().nullable(),
  /** Model the test called (the org model's provider id). */
  model: z.string().nullable(),
  message: z.string().nullable(),
  testedAt: IsoDate,
});
export const SetAiRoutesRequest = z.object({
  routes: z.array(z.object({ useCase: AiUseCase, modelId: Id.nullable() })).min(1).max(AI_USE_CASES.length),
  reason: Reason,
});

// ---------------------------------------------------------------- the org's model list (AI agents pick from it, ALP-83)
export const OrgModelOption = z.object({
  /** Stable reference to store on an agent: `platform:<alias>` or `org:<org model id>`. */
  ref: z.string(),
  source: z.enum(["platform", "org"]),
  /** Neutral display name (never the upstream provider). */
  name: z.string(),
  kind: AiModelKind,
  /** Use cases this model is the current route for. */
  usedFor: z.array(AiUseCase),
});
export const OrgModelList = z.object({
  byokEnabled: z.boolean(),
  models: z.array(OrgModelOption),
  /** Current route per use case (ref of the model that serves it). */
  routes: z.record(AiUseCase, z.string()),
});
export type OrgModelListT = z.infer<typeof OrgModelList>;

/** Neutral label for a platform alias: malfak-smart → "Malfak Smart". */
export function platformModelName(alias: string): string {
  const words = alias.replace(/^malfak-/, "").split(/[-_.]/).filter(Boolean);
  return ["Malfak", ...words.map((w) => (/^(tts|stt)$/i.test(w) ? w.toUpperCase() : w[0]!.toUpperCase() + w.slice(1)))].join(" ");
}

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const ById = z.object({ id: z.string().uuid() });
const ByConn = ById.extend({ connectionId: z.string().uuid() });

export const aiOrgRoutes = {
  // ---- AI activity (platform_admin; reads Langfuse with the server keys)
  adminAiTraces: route({ method: "GET", path: "/v1/admin/ai/traces", query: AiActivityQuery, response: AiTracePage, summary: "AI calls traced in Langfuse, per org and/or user, with filters (feature, model, status, time)" }),
  adminAiTrace: route({ method: "GET", path: "/v1/admin/ai/traces/:traceId", params: z.object({ traceId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/) }), response: AiTraceDetail, summary: "One AI trace: steps, model calls, tokens, cost, latency, scrubbed input/output" }),
  adminAiUsage: route({ method: "GET", path: "/v1/admin/ai/usage", query: AiUsageQuery, response: AiUsageSummary, summary: "AI usage (calls, tokens, cost) by feature, model and day, per org and/or user" }),
  // ---- BYOK + routing per org (platform_admin, audited; POSTs need an Idempotency-Key)
  adminOrgAiModels: route({ method: "GET", path: "/v1/admin/orgs/:id/ai/models", params: ById, response: OrgAiModelsAdmin, summary: "The org's provider connections (BYOK), routing per use case and spend split" }),
  adminCreateAiConnection: route({ method: "POST", path: "/v1/admin/orgs/:id/ai/connections", params: ById, body: CreateAiConnectionRequest, response: OrgAiModelsAdmin, status: 201, summary: "Add a client-owned provider connection (key encrypted; org-private gateway deployments)" }),
  adminUpdateAiConnection: route({ method: "PATCH", path: "/v1/admin/orgs/:id/ai/connections/:connectionId", params: ByConn, body: UpdateAiConnectionRequest, response: OrgAiModelsAdmin, summary: "Rename a connection or change its models" }),
  adminRotateAiConnection: route({ method: "POST", path: "/v1/admin/orgs/:id/ai/connections/:connectionId/rotate", params: ByConn, body: RotateAiConnectionRequest, response: OrgAiModelsAdmin, summary: "Replace the connection's provider key" }),
  adminTestAiConnection: route({ method: "POST", path: "/v1/admin/orgs/:id/ai/connections/:connectionId/test", params: ByConn, body: z.object({ modelId: Id.optional() }), response: AiConnectionTestResult, summary: "Tiny call through the gateway with the org's key (Test connection)" }),
  adminDeleteAiConnection: route({ method: "DELETE", path: "/v1/admin/orgs/:id/ai/connections/:connectionId", params: ByConn, response: OrgAiModelsAdmin, summary: "Delete a connection (its deployments are removed; routes fall back to the platform)" }),
  adminSetAiRoutes: route({ method: "PUT", path: "/v1/admin/orgs/:id/ai/routes", params: ById, body: SetAiRoutesRequest, response: OrgAiModelsAdmin, summary: "Route each use case to the platform model or one of the org's models" }),
  // ---- the org's allowed models (any member; AI agents pick from this list)
  orgAiModels: route({ method: "GET", path: "/v1/orgs/:orgId/ai/models", params: z.object({ orgId: z.string().uuid() }), response: OrgModelList, summary: "Models this org can use: platform aliases + the org's own models (neutral names)" }),
} as const;
